import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import type { ChartConfig, StatCardConfig } from "@hooks/use-reports/types";

export interface CreateUserReportDataSource {
  id: string;
  dbTable: string;
  groupBy: string[];
  sum?: string[];
  /** Static filter always applied to this data source (e.g. status = missing). */
  filter?: { column: string; values: string[] };
}

export interface CreateUserReportPayload {
  title: string;
  description?: string;
  /** Structural config — data source, grouping, and base charts (e.g. export table) */
  admin_config: {
    dataSources: CreateUserReportDataSource[];
    charts?: ChartConfig[];
  };
  /** Visualisation config — charts and stat cards editable via the normal user-config flow */
  override_config?: {
    charts?: Record<string, { hidden?: boolean; order?: number }>;
    statCards?: Record<string, { hidden?: boolean; order?: number }>;
    addedCharts?: Record<string, ChartConfig>;
    addedStatCards?: Record<string, StatCardConfig>;
  };
  report_filters?: string[];
}

/**
 * Hook to create a new user-generated report.
 * Calls: POST /wp-json/smassets/v1/reports/user-generated
 */
export const useCreateUserGeneratedReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const createReport = useCallback(
    async (payload: CreateUserReportPayload) => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl("wp-json/smassets/v1/reports/user-generated");

        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.authToken ?? ""}`,
            },
            body: JSON.stringify(payload),
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          throw new Error(`Failed to create report: ${response.status}`);
        }

        const result = await response.json();
        setLoading(false);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create report";
        setError(errorMessage);
        setLoading(false);
        return null;
      }
    },
    [user?.authToken, user?.refreshToken, getApiUrl, refreshUserToken]
  );

  return { createReport, loading, error };
};
